"use client";

/**
 * The console's side of the native mobile app, once it is signed in.
 *
 * Signing in happens earlier, in `authService` (`adoptNativeSession`): it
 * exchanges the token the app injected before auth reports ready, so no route
 * ever sees a signed-out moment. This tells the app when it may open a page
 * here, and opens the ones it asks for.
 *
 * Mounted in the root layout so it runs on whatever route the app opens. In an
 * ordinary browser it renders nothing and does nothing.
 */
import { useEffect, useRef, useState } from "react";

import { usePathname, useRouter } from "next/navigation";

import {
  NATIVE_NAVIGATE_EVENT,
  isNativeHost,
  nativeNavigationPath,
  signalNativeReady,
} from "@/lib/native-bridge";

import { authService } from "@/services/auth-service";

export function NativeAuthBridge(): null {
  const router = useRouter();
  const pathname = usePathname();
  const [signedIn, setSignedIn] = useState(false);
  const readySentRef = useRef(false);

  // Ready means the app may now open a page here (a tapped notification, an
  // answered call's patient). Not while on /login: that page sends a signed-in
  // user on to their home page, which would replace the page the app opened.
  useEffect(() => {
    if (!signedIn || readySentRef.current) return;
    if (pathname?.startsWith("/login")) return;
    readySentRef.current = true;
    signalNativeReady();
  }, [signedIn, pathname]);

  // The app asks for a page when the user answers a call on the phone (the
  // caller's patient profile). Client-side navigation, not a reload, so the
  // console keeps its state. Same-site paths only: anything else is dropped.
  useEffect(() => {
    if (!isNativeHost()) return;
    const onNavigate = (event: Event) => {
      const path = nativeNavigationPath(event);
      if (path) router.push(path);
    };
    window.addEventListener(NATIVE_NAVIGATE_EVENT, onNavigate);
    return () => window.removeEventListener(NATIVE_NAVIGATE_EVENT, onNavigate);
  }, [router]);

  useEffect(() => {
    if (!isNativeHost()) return;
    return authService.addAuthStateListener((user) => setSignedIn(!!user));
  }, []);

  return null;
}
